/**
 * ═══════════════════════════════════════════════════════════════════════════
 * PFP JEWELLERY - GSAP LUXURY ANIMATIONS
 * Handcrafted animations for an editorial luxury experience
 * ═══════════════════════════════════════════════════════════════════════════
 */

(function() {
  'use strict';

  // Wait for GSAP to be available
  const initLuxuryAnimations = () => {
    if (typeof gsap === 'undefined' || typeof ScrollTrigger === 'undefined') {
      console.warn('GSAP or ScrollTrigger not loaded');
      return;
    }

    // Register ScrollTrigger plugin
    gsap.registerPlugin(ScrollTrigger);

    // ─────────────────────────────────────────────────────────────────────────
    // CONFIGURATION
    // ─────────────────────────────────────────────────────────────────────────
    const config = {
      ease: 'power3.out',
      easeSmooth: 'power2.out',
      easeElastic: 'elastic.out(1, 0.5)',
      duration: 1,
      durationFast: 0.6,
      staggerAmount: 0.1,
      scrollTriggerStart: 'top 85%',
      scrollTriggerEnd: 'bottom 20%'
    };

    // ─────────────────────────────────────────────────────────────────────────
    // HERO SECTION ANIMATIONS
    // ─────────────────────────────────────────────────────────────────────────
    const initHeroAnimations = () => {
      const hero = document.querySelector('.luxury-hero');
      if (!hero) return;

      const logo = hero.querySelector('.luxury-hero__logo');
      const tagline = hero.querySelector('.luxury-hero__tagline');
      const motto = hero.querySelector('.luxury-hero__motto');
      const divider = hero.querySelector('.luxury-hero__divider');
      const credentials = hero.querySelector('.luxury-hero__credentials');
      const scrollIndicator = hero.querySelector('.luxury-hero__scroll-indicator');

      // Initial timeline for hero entrance
      const heroTimeline = gsap.timeline({
        defaults: { ease: config.ease }
      });

      // Animate hero elements in sequence
      heroTimeline
        .to(logo, {
          opacity: 1,
          y: 0,
          duration: 1.2,
          onComplete: () => logo?.classList.add('is-visible')
        })
        .to(tagline, {
          opacity: 1,
          y: 0,
          duration: 0.8,
          onComplete: () => tagline?.classList.add('is-visible')
        }, '-=0.6')
        .to(divider, {
          opacity: 1,
          scaleX: 1,
          duration: 0.8,
          onComplete: () => divider?.classList.add('is-visible')
        }, '-=0.4')
        .to(motto, {
          opacity: 1,
          y: 0,
          duration: 1,
          onComplete: () => motto?.classList.add('is-visible')
        }, '-=0.5')
        .to(credentials, {
          opacity: 0.7,
          y: 0,
          duration: 0.8,
          onComplete: () => credentials?.classList.add('is-visible')
        }, '-=0.6')
        .to(scrollIndicator, {
          opacity: 1,
          duration: 0.8,
          onComplete: () => scrollIndicator?.classList.add('is-visible')
        }, '-=0.3');
    };

    // ─────────────────────────────────────────────────────────────────────────
    // LOGO SCROLL BEHAVIOR (Simplified - just handle header state)
    // ─────────────────────────────────────────────────────────────────────────
    const initLogoScrollBehavior = () => {
      const header = document.querySelector('.section-header');
      const hero = document.querySelector('.luxury-hero');
      
      if (!hero) return;

      // Add class to body when hero is active
      document.body.classList.add('hero-active');
      document.body.classList.add('hero-in-view');

      // Create scroll trigger for header state only
      ScrollTrigger.create({
        trigger: hero,
        start: 'top top',
        end: 'bottom top',
        onUpdate: (self) => {
          const progress = self.progress;
          
          if (progress > 0.3) {
            header?.classList.add('is-scrolled');
            document.body.classList.remove('hero-active');
            document.body.classList.remove('hero-in-view');
          } else {
            header?.classList.remove('is-scrolled');
            document.body.classList.add('hero-active');
            document.body.classList.add('hero-in-view');
          }
        },
        onLeave: () => {
          header?.classList.add('is-scrolled');
          document.body.classList.remove('hero-active');
          document.body.classList.remove('hero-in-view');
        },
        onEnterBack: () => {
          // When scrolling back to top
          document.body.classList.add('hero-in-view');
        }
      });

      // Header sticky behavior
      ScrollTrigger.create({
        trigger: hero,
        start: 'bottom top',
        end: 'max',
        onUpdate: () => {
          header?.classList.add('is-scrolled');
          document.body.classList.remove('hero-in-view');
        },
        onLeaveBack: () => {
          if (window.scrollY <= hero.offsetHeight * 0.3) {
            header?.classList.remove('is-scrolled');
            document.body.classList.add('hero-in-view');
          }
        }
      });
    };

    // ─────────────────────────────────────────────────────────────────────────
    // SCROLL-TRIGGERED ANIMATIONS
    // ─────────────────────────────────────────────────────────────────────────
    const initScrollAnimations = () => {
      // Fade Up Animation
      gsap.utils.toArray('.gsap-fade-up, [data-animate="fade-up"]').forEach(element => {
        gsap.fromTo(element, 
          { opacity: 0, y: 60 },
          {
            opacity: 1,
            y: 0,
            duration: config.duration,
            ease: config.ease,
            scrollTrigger: {
              trigger: element,
              start: config.scrollTriggerStart,
              toggleActions: 'play none none reverse'
            }
          }
        );
      });

      // Fade In Animation
      gsap.utils.toArray('.gsap-fade-in, [data-animate="fade-in"]').forEach(element => {
        gsap.fromTo(element,
          { opacity: 0 },
          {
            opacity: 1,
            duration: config.duration,
            ease: config.easeSmooth,
            scrollTrigger: {
              trigger: element,
              start: config.scrollTriggerStart,
              toggleActions: 'play none none reverse'
            }
          }
        );
      });

      // Slide from Left
      gsap.utils.toArray('.gsap-slide-left, [data-animate="slide-left"]').forEach(element => {
        gsap.fromTo(element,
          { opacity: 0, x: -60 },
          {
            opacity: 1,
            x: 0,
            duration: config.duration,
            ease: config.ease,
            scrollTrigger: {
              trigger: element,
              start: config.scrollTriggerStart,
              toggleActions: 'play none none reverse'
            }
          }
        );
      });

      // Slide from Right
      gsap.utils.toArray('.gsap-slide-right, [data-animate="slide-right"]').forEach(element => {
        gsap.fromTo(element,
          { opacity: 0, x: 60 },
          {
            opacity: 1,
            x: 0,
            duration: config.duration,
            ease: config.ease,
            scrollTrigger: {
              trigger: element,
              start: config.scrollTriggerStart,
              toggleActions: 'play none none reverse'
            }
          }
        );
      });

      // Scale Up Animation
      gsap.utils.toArray('.gsap-scale-up, [data-animate="scale-up"]').forEach(element => {
        gsap.fromTo(element,
          { opacity: 0, scale: 0.9 },
          {
            opacity: 1,
            scale: 1,
            duration: config.duration,
            ease: config.ease,
            scrollTrigger: {
              trigger: element,
              start: config.scrollTriggerStart,
              toggleActions: 'play none none reverse'
            }
          }
        );
      });

      // Reveal Line Animation
      gsap.utils.toArray('.gsap-reveal-line, [data-animate="reveal-line"]').forEach(element => {
        gsap.fromTo(element,
          { scaleX: 0, transformOrigin: 'left' },
          {
            scaleX: 1,
            duration: config.durationFast,
            ease: config.easeSmooth,
            scrollTrigger: {
              trigger: element,
              start: config.scrollTriggerStart,
              toggleActions: 'play none none reverse'
            }
          }
        );
      });

      // Stagger Children Animation
      gsap.utils.toArray('.gsap-stagger-children, [data-animate="stagger"]').forEach(container => {
        const children = container.children;
        gsap.fromTo(children,
          { opacity: 0, y: 40 },
          {
            opacity: 1,
            y: 0,
            duration: config.durationFast,
            ease: config.ease,
            stagger: config.staggerAmount,
            scrollTrigger: {
              trigger: container,
              start: config.scrollTriggerStart,
              toggleActions: 'play none none reverse'
            }
          }
        );
      });

      // Mask Reveal Animation
      gsap.utils.toArray('.gsap-mask-reveal, [data-animate="mask-reveal"]').forEach(element => {
        gsap.fromTo(element,
          { clipPath: 'inset(0 100% 0 0)' },
          {
            clipPath: 'inset(0 0% 0 0)',
            duration: config.duration,
            ease: config.ease,
            scrollTrigger: {
              trigger: element,
              start: config.scrollTriggerStart,
              toggleActions: 'play none none reverse'
            }
          }
        );
      });
    };

    // ─────────────────────────────────────────────────────────────────────────
    // COLLECTION/PRODUCT CARD ANIMATIONS
    // ─────────────────────────────────────────────────────────────────────────
    const initCardAnimations = () => {
      // Animate collection cards
      gsap.utils.toArray('.collection-list, .featured-collection').forEach(section => {
        const cards = section.querySelectorAll('.card, .collection-list__item, .card-wrapper');
        
        gsap.fromTo(cards,
          { opacity: 0, y: 50 },
          {
            opacity: 1,
            y: 0,
            duration: 0.8,
            ease: config.ease,
            stagger: 0.15,
            scrollTrigger: {
              trigger: section,
              start: 'top 80%',
              toggleActions: 'play none none reverse'
            }
          }
        );
      });

      // Product cards hover enhancement
      document.querySelectorAll('.card-wrapper, .product-card-wrapper').forEach(card => {
        const image = card.querySelector('img');
        
        card.addEventListener('mouseenter', () => {
          gsap.to(image, {
            scale: 1.05,
            duration: 0.6,
            ease: config.easeSmooth
          });
        });

        card.addEventListener('mouseleave', () => {
          gsap.to(image, {
            scale: 1,
            duration: 0.6,
            ease: config.easeSmooth
          });
        });
      });
    };

    // ─────────────────────────────────────────────────────────────────────────
    // SECTION TITLE ANIMATIONS
    // ─────────────────────────────────────────────────────────────────────────
    const initSectionTitleAnimations = () => {
      gsap.utils.toArray('.section-header__title, .title, .collection-list-title, h2.h1, h2.h2').forEach(title => {
        gsap.fromTo(title,
          { opacity: 0, y: 30 },
          {
            opacity: 1,
            y: 0,
            duration: 0.8,
            ease: config.ease,
            scrollTrigger: {
              trigger: title,
              start: 'top 85%',
              toggleActions: 'play none none reverse'
            }
          }
        );
      });
    };

    // ─────────────────────────────────────────────────────────────────────────
    // PARALLAX EFFECTS
    // ─────────────────────────────────────────────────────────────────────────
    const initParallaxEffects = () => {
      // Subtle parallax on images
      gsap.utils.toArray('[data-parallax]').forEach(element => {
        const depth = element.dataset.parallax || 0.2;
        
        gsap.to(element, {
          yPercent: -30 * depth,
          ease: 'none',
          scrollTrigger: {
            trigger: element,
            start: 'top bottom',
            end: 'bottom top',
            scrub: true
          }
        });
      });
    };

    // ─────────────────────────────────────────────────────────────────────────
    // SMOOTH SCROLL ENHANCEMENT
    // ─────────────────────────────────────────────────────────────────────────
    const initSmoothScroll = () => {
      document.querySelectorAll('a[href^="#"]').forEach(anchor => {
        anchor.addEventListener('click', function(e) {
          const href = this.getAttribute('href');
          if (href === '#') return;
          
          const target = document.querySelector(href);
          if (target) {
            e.preventDefault();
            gsap.to(window, {
              scrollTo: { y: target, offsetY: 100 },
              duration: 1,
              ease: config.ease
            });
          }
        });
      });

      // Scroll indicator click
      const scrollIndicator = document.querySelector('.luxury-hero__scroll-indicator');
      if (scrollIndicator) {
        scrollIndicator.addEventListener('click', () => {
          const nextSection = document.querySelector('.luxury-hero')?.nextElementSibling;
          if (nextSection) {
            gsap.to(window, {
              scrollTo: { y: nextSection, offsetY: 0 },
              duration: 1.2,
              ease: config.ease
            });
          }
        });
      }
    };

    // ─────────────────────────────────────────────────────────────────────────
    // PAGE TRANSITION EFFECTS
    // ─────────────────────────────────────────────────────────────────────────
    const initPageTransitions = () => {
      // Fade in on page load
      gsap.fromTo('body', 
        { opacity: 0 },
        { opacity: 1, duration: 0.6, ease: config.easeSmooth }
      );

      // Handle internal links
      document.querySelectorAll('a:not([href^="#"]):not([target="_blank"])').forEach(link => {
        link.addEventListener('click', function(e) {
          const href = this.getAttribute('href');
          if (href && !href.startsWith('http') && !href.startsWith('mailto:')) {
            e.preventDefault();
            gsap.to('body', {
              opacity: 0,
              duration: 0.3,
              ease: config.easeSmooth,
              onComplete: () => {
                window.location.href = href;
              }
            });
          }
        });
      });
    };

    // ─────────────────────────────────────────────────────────────────────────
    // INITIALIZE ALL ANIMATIONS
    // ─────────────────────────────────────────────────────────────────────────
    const init = () => {
      initHeroAnimations();
      initLogoScrollBehavior();
      initScrollAnimations();
      initCardAnimations();
      initSectionTitleAnimations();
      initParallaxEffects();
      initSmoothScroll();
      initPageTransitions();

      // Refresh ScrollTrigger on resize
      let resizeTimeout;
      window.addEventListener('resize', () => {
        clearTimeout(resizeTimeout);
        resizeTimeout = setTimeout(() => {
          ScrollTrigger.refresh();
        }, 250);
      });

      console.log('✨ PFP Luxury Animations initialized');
    };

    // Run initialization
    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', init);
    } else {
      init();
    }
  };

  // Load GSAP dynamically if not present
  const loadGSAP = () => {
    if (typeof gsap !== 'undefined' && typeof ScrollTrigger !== 'undefined') {
      initLuxuryAnimations();
      return;
    }

    // Load GSAP
    const gsapScript = document.createElement('script');
    gsapScript.src = 'https://cdnjs.cloudflare.com/ajax/libs/gsap/3.12.2/gsap.min.js';
    gsapScript.onload = () => {
      // Load ScrollTrigger
      const scrollTriggerScript = document.createElement('script');
      scrollTriggerScript.src = 'https://cdnjs.cloudflare.com/ajax/libs/gsap/3.12.2/ScrollTrigger.min.js';
      scrollTriggerScript.onload = () => {
        // Load ScrollTo plugin
        const scrollToScript = document.createElement('script');
        scrollToScript.src = 'https://cdnjs.cloudflare.com/ajax/libs/gsap/3.12.2/ScrollToPlugin.min.js';
        scrollToScript.onload = initLuxuryAnimations;
        document.head.appendChild(scrollToScript);
      };
      document.head.appendChild(scrollTriggerScript);
    };
    document.head.appendChild(gsapScript);
  };

  // Initialize
  loadGSAP();
})();
